import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  ArrowRight,
  BadgeCheck,
  FlagTriangleRight,
  ShieldCheck,
  UserRound,
} from "lucide-react"
import { useState } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { PublicPagePending } from "@/components/loading-skeleton"
import { PublicHeader } from "@/components/public-header"
import { Button } from "@/components/ui/button"
import { postLoginPath, safeInternalPath } from "@/lib/navigation"
import { demoPageQueryOptions } from "@/lib/query-options"
import { cn } from "@/lib/utils"
import { signInDemoFn } from "@/server/functions"

type DemoSearch = { next?: string }

export const Route = createFileRoute("/demo")({
  validateSearch: (search: Record<string, unknown>): DemoSearch => ({
    next: safeInternalPath(search.next),
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(demoPageQueryOptions()),
  head: () => ({
    meta: [{ title: "Choose a demo account · Groundline" }],
  }),
  pendingComponent: PublicPagePending,
  component: DemoAccess,
})

function DemoAccess() {
  const { data } = useSuspenseQuery(demoPageQueryOptions())
  const { viewer, accounts } = data
  const { next } = Route.useSearch()
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [error, setError] = useState("")

  async function enterDemo(
    userId: "user-demo-golfer" | "user-demo-staff" | "user-demo-manager",
    role: "golfer" | "staff" | "manager"
  ) {
    setPendingId(userId)
    setError("")

    try {
      await signInDemoFn({ data: { userId } })
      window.location.assign(postLoginPath(next, userId, role))
    } catch {
      setError("That demo session could not be started. Please try again.")
      setPendingId(null)
    }
  }

  return (
    <>
      <PublicHeader viewer={viewer} />
      <DemoNotice />
      <main className="site-frame py-12 sm:py-16">
        <div className="mx-auto max-w-[840px]">
          <div className="max-w-[650px]">
            <span className="eyebrow">
              <BadgeCheck className="size-4" />
              One-click access
            </span>
            <h1 className="page-title">Choose a side of the desk.</h1>
            <p className="page-lede">
              Guest checkout does not require an account. Enter as a golfer to
              manage rounds and a Stripe Player Pass, as pro shop staff to
              settle on-course purchases, or as a manager to run the day.
            </p>
          </div>

          {error ? (
            <p className="action-error mt-6" role="alert">
              {error}
            </p>
          ) : null}

          <section className="ruled-panel mt-9 overflow-hidden">
            <div className="ruled-panel__head">
              <div>
                <span className="eyebrow">Demo identities</span>
                <h2 className="mt-1 text-sm font-semibold">
                  Choose a role to explore the booking flow
                </h2>
              </div>
              <FlagTriangleRight
                className="size-5 text-primary"
                aria-hidden="true"
              />
            </div>
            {accounts.map((account) => {
              const isCurrent = viewer?.id === account.id
              const isPending = pendingId === account.id
              const AccountIcon =
                account.role === "golfer" ? UserRound : ShieldCheck

              return (
                <div
                  key={account.id}
                  className="rule-row grid gap-4 p-5 sm:grid-cols-[48px_1fr_auto] sm:items-center"
                >
                  <span
                    className={cn(
                      "grid size-12 place-items-center rounded-[10px] border",
                      account.role === "golfer"
                        ? "border-border bg-secondary text-primary"
                        : "border-primary bg-primary text-white"
                    )}
                    aria-hidden="true"
                  >
                    <AccountIcon className="size-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-display text-[1.45rem] leading-none font-semibold uppercase">
                        {account.name}
                      </h2>
                      <span className="status-chip">
                        {account.role === "golfer"
                          ? "Golfer"
                          : account.role === "manager"
                            ? "Manager"
                            : "Pro shop"}
                      </span>
                      {isCurrent ? (
                        <span className="status-chip status-chip--checked_in">
                          Current
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {account.email}
                    </p>
                    <p className="mt-2 max-w-[52ch] text-xs leading-5 text-muted-foreground">
                      {account.role === "golfer"
                        ? "Create account-linked bookings, pay in Stripe test mode, and start a recurring Player Pass."
                        : account.role === "staff"
                          ? "Settle food, beverage, and add-on spending for checked-in and on-course groups."
                          : "Review course operations, customer records, bookings, and the seven-day utilization view."}
                    </p>
                  </div>
                  <Button
                    variant={account.role === "golfer" ? "outline" : "accent"}
                    disabled={pendingId !== null}
                    onClick={() =>
                      enterDemo(
                        account.id as
                          | "user-demo-golfer"
                          | "user-demo-staff"
                          | "user-demo-manager",
                        account.role
                      )
                    }
                  >
                    {isPending
                      ? "Starting…"
                      : isCurrent
                        ? "Continue"
                        : account.role === "staff"
                          ? "Enter pro shop"
                          : `Enter as ${account.role}`}
                    <ArrowRight data-icon="inline-end" />
                  </Button>
                </div>
              )
            })}
          </section>

          <p className="mt-5 text-xs leading-5 text-muted-foreground">
            Demo access lasts seven days. Choosing another role switches this
            browser to that account.
          </p>
        </div>
      </main>
    </>
  )
}
